import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Typography, Avatar, Dropdown, Select, Badge, Modal, Form, Input, message, Progress, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  DashboardOutlined,
  FileOutlined,
  SettingOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  CarOutlined,
  ToolOutlined,
  ExperimentOutlined,
  BankOutlined,
  IdcardOutlined,
  SafetyOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { tenantService } from '../services/tenants';
import { itemService } from '../services/items';
import { documentService } from '../services/documents';
import { userService } from '../services/users';
import '../pages/Dashboard.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuKeyToPath = {
  '1': '/dashboard',
  '2': '/documents',
  '3': '/companies',
  '4': '/workers',
  '5': '/vehicles',
  '6': '/equipments',
  '7': '/chemicals',
  '8': '/calendar',
  '9': '/settings',
  'admin-1': '/admin/document-types',
  'admin-2': '/admin/users',
  'admin-3': '/admin/backups',
};

const getMenuItems = (userRole, stats = {}) => {
  const renderLabel = (label, total, expiring) => (
    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <span>{label}</span>
      <span style={{ display: 'flex', gap: 4 }}>
        {total > 0 && (
          <Badge 
            count={total} 
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontSize: 11 }}
          />
        )}
        {expiring > 0 && (
          <Badge 
            count={expiring} 
            style={{ backgroundColor: '#faad14', color: 'white', fontSize: 11 }}
          />
        )}
      </span>
    </span>
  );

  const baseItems = [
    { key: '1', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '2', icon: <FileOutlined />, label: renderLabel('Documenti', stats.documents?.total, stats.documents?.expiring) },
    { key: '3', icon: <BankOutlined />, label: renderLabel('Aziende', stats.companies?.total, stats.companies?.expiring) },
    { key: '4', icon: <IdcardOutlined />, label: renderLabel('Lavoratori', stats.workers?.total, stats.workers?.expiring) },
    { key: '5', icon: <CarOutlined />, label: renderLabel('Veicoli', stats.vehicles?.total, stats.vehicles?.expiring) },
    { key: '6', icon: <ToolOutlined />, label: renderLabel('Attrezzature', stats.equipments?.total, stats.equipments?.expiring) },
    { key: '7', icon: <ExperimentOutlined />, label: renderLabel('Prodotti Chimici', stats.chemicals?.total, stats.chemicals?.expiring) },
  ];

  // Settings menu item - only for admin and qhse_nke
  if (userRole === 'admin' || userRole === 'qhse_nke') {
    baseItems.push({ key: '9', icon: <SettingOutlined />, label: 'Impostazioni' });
  }

  if (userRole === 'admin' || userRole === 'qhse_nke') {
    const adminChildren = [
      { key: 'admin-1', label: 'Tipologie Documento' },
      { key: 'admin-2', label: 'Utenti' },
    ];

    if (userRole === 'admin') {
      adminChildren.push({ key: 'admin-3', label: 'Backup' });
    }

    baseItems.push({
      key: 'admin',
      icon: <SafetyOutlined />,
      label: 'Admin',
      children: adminChildren,
    });
  }

  return baseItems;
};

function MainLayout({ title, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedTenantId, selectedTenantName, setTenantSelection, canSelectTenant } = useTenant();
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Password validation (must match backend)
  const PASSWORD_POLICY = {
    minLength: 10,
    requireUppercase: true,
    requireLowercase: true,
    requireDigit: true,
    requireSpecial: true,
    allowedSpecialChars: "!@#$%^&*()_+-={}[]:;\"'<>,.?/",
  };

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= PASSWORD_POLICY.minLength,
      uppercase: !PASSWORD_POLICY.requireUppercase || /[A-Z]/.test(password),
      lowercase: !PASSWORD_POLICY.requireLowercase || /[a-z]/.test(password),
      digit: !PASSWORD_POLICY.requireDigit || /\d/.test(password),
      special: !PASSWORD_POLICY.requireSpecial || new RegExp(`[${PASSWORD_POLICY.allowedSpecialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`).test(password),
      noSpaces: !/\s/.test(password),
    };
    const isValid = Object.values(checks).every(v => v);
    const strength = Object.values(checks).filter(v => v).length / Object.keys(checks).length;
    return { checks, isValid, strength };
  };

  const newPassword = Form.useWatch('new_password', passwordForm);
  const { checks, isValid: isPasswordValid, strength } = useMemo(
    () => validatePassword(newPassword || ''),
    [newPassword]
  );

  const loadTenants = useCallback(async () => {
    if (tenantsLoading) return;
    setTenantsLoading(true);
    try {
      const res = await tenantService.list();
      setTenants(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTenantsLoading(false);
    }
  }, [tenantsLoading]);

  useEffect(() => {
    if (canSelectTenant && tenants.length === 0) {
      loadTenants();
    }
  }, [canSelectTenant, tenants.length, loadTenants]);

  useEffect(() => {
    loadStats();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId || selectedTenantName) return;
    const t = tenants.find((x) => x.id === selectedTenantId);
    if (t) {
      setTenantSelection(selectedTenantId, t.name);
    }
  }, [selectedTenantId, selectedTenantName, tenants, setTenantSelection]);

  const loadStats = async () => {
    try {
      const params = {};
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      
      const [documents, vehicles, equipments, chemicals, workers, companies] = await Promise.all([
        documentService.list(params),
        itemService.list('vehicle', params),
        itemService.list('equipment', params),
        itemService.list('chemical', params),
        itemService.list('worker', params),
        itemService.list('company', params),
      ]);

      const today = dayjs();
      const in15Days = today.add(15, 'day');

      const countExpiring = (items) => {
        return items.filter(item => {
          if (!item.expiration_date) return false;
          const expDate = dayjs(item.expiration_date);
          return expDate.isAfter(today) && expDate.isBefore(in15Days);
        }).length;
      };

      const getItemExpiring = (itemList, itemType) => {
        let expiring = 0;
        const docsForType = documents.filter(doc => doc.item?.item_type === itemType);
        docsForType.forEach(doc => {
          if (doc.expiration_date) {
            const expDate = dayjs(doc.expiration_date);
            if (expDate.isAfter(today) && expDate.isBefore(in15Days)) {
              expiring++;
            }
          }
        });
        return expiring;
      };

      setStats({
        documents: { total: documents.length, expiring: countExpiring(documents) },
        vehicles: { total: vehicles.length, expiring: getItemExpiring(vehicles, 'vehicle') },
        equipments: { total: equipments.length, expiring: getItemExpiring(equipments, 'equipment') },
        chemicals: { total: chemicals.length, expiring: getItemExpiring(chemicals, 'chemical') },
        workers: { total: workers.length, expiring: getItemExpiring(workers, 'worker') },
        companies: { total: companies.length, expiring: getItemExpiring(companies, 'company') },
      });
    } catch (error) {
      console.error('Errore caricamento stats menu:', error);
    }
  };

  const selectedKeys = useMemo(() => {
    const entry = Object.entries(menuKeyToPath).find(([, path]) => location.pathname.startsWith(path));
    return [entry ? entry[0] : '1'];
  }, [location.pathname]);

  const menuItems = useMemo(() => getMenuItems(user?.role, stats), [user?.role, stats]);

  const handleChangePassword = async (values) => {
    if (values.new_password !== values.confirm_password) {
      message.error('Le password non coincidono');
      return;
    }

    if (!isPasswordValid) {
      message.error('Password non conforme ai requisiti di sicurezza');
      return;
    }

    setPasswordLoading(true);
    try {
      await userService.changePassword(user.id, {
        old_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('Password cambiata con successo');
      setChangePasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Errore nel cambio password';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setPasswordLoading(false);
    }
  };

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Cambia Password',
      onClick: () => setChangePasswordModalOpen(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={250} className="glass-sider" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="logo" style={{ background: 'rgba(255,255,255,0.90)' }}>
          <img
            src="/logo_nke.png"
            alt="NKE Logo"
            style={{ display: 'block', margin: '0 auto 8px', maxWidth: '80%', maxHeight: 48, objectFit: 'contain' }}
          />
          <img
            src="/logo_app.png?v=2"
            alt="Arkivio"
            style={{ display: 'block', margin: '8px auto 0', width: '100%', maxWidth: '100%', height: 'auto' }}
          />
        </div>
        {canSelectTenant && (
          <div className="glass-select">
            <Select
              labelInValue
              value={(() => {
                if (!selectedTenantId) return { value: 'all', label: 'Tutti i tenant' };
                const t = tenants.find((x) => x.id === selectedTenantId);
                return { value: selectedTenantId, label: t?.name || selectedTenantName || String(selectedTenantId) };
              })()}
              onChange={(opt) => {
                const value = opt?.value;
                const label = opt?.label;
                if (value === 'all') {
                  setTenantSelection(null, null);
                } else {
                  setTenantSelection(value, label);
                }
              }}
              placeholder="Seleziona tenant"
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              loading={tenantsLoading}
              notFoundContent={tenantsLoading ? 'Caricamento...' : undefined}
              onDropdownVisibleChange={(open) => {
                if (open && tenants.length === 0) {
                  loadTenants();
                }
              }}
            >
              <Select.Option value="all">Tutti i tenant</Select.Option>
              {tenants.map(t => (
                <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
              ))}
            </Select>
          </div>
        )}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={({ key }) => {
            const dest = menuKeyToPath[key] || '/dashboard';
            navigate(dest);
          }}
          items={menuItems}
          className="glass-menu"
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text strong>{user?.full_name || 'Utente'}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{user?.role || ''}</Text>
              </div>
            </div>
          </Dropdown>
        </Header>
        <Content style={{ margin: '24px' }}>
          {children}
        </Content>
      </Layout>

      <Modal
        title="Cambia Password"
        open={changePasswordModalOpen}
        onCancel={() => {
          setChangePasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        onOk={() => passwordForm.submit()}
        confirmLoading={passwordLoading}
        okText="Cambia"
        cancelText="Annulla"
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="current_password"
            label="Password Attuale"
            rules={[{ required: true, message: 'Inserisci la password attuale' }]}
          >
            <Input.Password placeholder="Password attuale" size="large" />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="Nuova Password"
            rules={[{ required: true, message: 'Inserisci la nuova password' }]}
          >
            <Input.Password placeholder="Nuova password" size="large" />
          </Form.Item>

          {newPassword && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Complessità Password</Text>
              </div>
              <Progress
                percent={Math.round(strength * 100)}
                size="small"
                status={isPasswordValid ? 'success' : 'active'}
                strokeColor={isPasswordValid ? '#52c41a' : '#1890ff'}
              />
              <Space direction="vertical" size="small" style={{ marginTop: 12, width: '100%' }}>
                {Object.entries({
                  length: `Almeno ${PASSWORD_POLICY.minLength} caratteri`,
                  uppercase: 'Almeno una maiuscola (A-Z)',
                  lowercase: 'Almeno una minuscola (a-z)',
                  digit: 'Almeno una cifra (0-9)',
                  special: `Almeno un carattere speciale: ${PASSWORD_POLICY.allowedSpecialChars}`,
                  noSpaces: 'Nessuno spazio',
                }).map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    {checks[key] ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#bfbfbf' }} />
                    )}
                    <span style={{ color: checks[key] ? '#52c41a' : '#bfbfbf' }}>{label}</span>
                  </div>
                ))}
              </Space>
            </div>
          )}

          <Form.Item
            name="confirm_password"
            label="Conferma Password"
            rules={[{ required: true, message: 'Conferma la nuova password' }]}
          >
            <Input.Password placeholder="Conferma password" size="large" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default MainLayout;
